/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
/**
 * Button component to be used in the toolbar.
 * @element a3d-button
 * @slot icon - Slot for the button icon
 *
 * @cssprop --a3d-button-background-color-override - Background color of the button
 * @cssprop --a3d-button-border-color-override - Border color of the button
 * @cssprop --a3d-button-content-color-override - Content color of the button
 * @cssprop --a3d-button-background-color-hover-override - Background color of the button on hover
 * @cssprop --a3d-button-border-color-hover-override - Border color of the button on hover
 * @cssprop --a3d-button-content-color-hover-override - Content color of the button on hover
 * @cssprop --a3d-button-background-color-selected-override - Background color of the button when selected
 * @cssprop --a3d-button-border-color-selected-override - Border color of the button when selected
 * @cssprop --a3d-button-content-color-selected-override - Content color of the button when selected
 * @cssprop --a3d-button-background-color-selected-hover-override - Background color of the button when selected and on hover
 * @cssprop --a3d-button-border-color-selected-hover-override - Border color of the button when selected and on hover
 * @cssprop --a3d-button-content-color-selected-hover-override - Content color of the button when selected and on hover
 * @cssprop --a3d-button-background-color-disabled-override - Background color of the button when disabled
 */
let A3dButton = class A3dButton extends LitElement {
    constructor() {
        super(...arguments);
        this.selected = false;
        this.value = '';
        this.disabled = false;
        this.onClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.disabled) {
                return;
            }
            this.dispatchEvent(new CustomEvent('click', { bubbles: true, detail: this.value }));
        };
    }
    render() {
        return html `
      <button @click=${this.onClick}>
        <slot name="icon"></slot>
        <slot></slot>
      </button>
    `;
    }
};
A3dButton.styles = css `
    @media (prefers-color-scheme: light) {
      :host {
        --a3d-button-background-color: var(
          --a3d-button-background-color-override,
          var(--spectrum-actionbutton-quiet-background-color, transparent)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-content-color-override,
          var(--spectrum-alias-text-color, #222222)
        );
      }

      :host(:hover) {
        --a3d-button-background-color: var(
          --a3d-button-background-color-hover-override,
          var(--spectrum-gray-200, #3f3f3f)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-hover-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-content-color-hover-override,
          var(--spectrum-gray-900, #000000)
        );
      }

      :host([selected]) {
        --a3d-button-background-color: var(
          --a3d-button-background-color-selected-override,
          var(--spectrum-gray-700, #464646)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-selected-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-background-color-selected-override,
          var(--spectrum-gray-75, #fdfdfd)
        );
      }

      :host([selected]:hover) {
        --a3d-button-background-color: var(
          --a3d-button-background-color-selected-hover-override,
          var(--spectrum-gray-800, #222222)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-selected-hover-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-content-color-selected-hover-override,
          var(--spectrum-gray-75, #fdfdfd)
        );
      }

      :host([disabled]) {
        --a3d-button-background-color: var(
          --a3d-button-background-color-override,
          var(--spectrum-actionbutton-quiet-background-color, transparent)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-background-color-disabled-override,
          var(--spectrum-gray-400, #c6c6c6)
        );
      }

      :host([disabled]:hover) {
        --a3d-button-background-color: var(
          --a3d-button-background-color-override,
          var(--spectrum-actionbutton-quiet-background-color, transparent)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-content-color-disabled-hover-override,
          var(--spectrum-gray-400, #c6c6c6)
        );
      }
    }

    @media (prefers-color-scheme: dark) {
      :host {
        --a3d-button-background-color: var(
          --a3d-button-background-color-override,
          var(--spectrum-actionbutton-quiet-background-color, transparent)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-content-color-override,
          var(--spectrum-gray-800, #ebebeb)
        );
      }

      :host(:hover) {
        --a3d-button-background-color: var(
          --a3d-button-background-color-hover-override,
          var(--spectrum-gray-200, #3f3f3f)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-hover-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-content-color-hover-override,
          var(--spectrum-gray-50, #222222)
        );
      }

      :host([selected]) {
        --a3d-button-background-color: var(
          --a3d-button-background-color-selected-override,
          var(--spectrum-gray-700, #d1d1d1)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-selected-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-background-color-selected-override,
          var(--spectrum-gray-75, #262626)
        );
      }

      :host([selected]:hover) {
        --a3d-button-background-color: var(
          --a3d-button-background-color-selected-hover-override,
          var(--spectrum-gray-800, #ebebeb)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-selected-hover-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-content-color-selected-hover-override,
          var(--spectrum-gray-75, #262626)
        );
      }

      :host([disabled]) {
        --a3d-button-background-color: var(
          --a3d-button-background-color-override,
          var(--spectrum-actionbutton-quiet-background-color, transparent)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-background-color-disabled-override,
          var(--spectrum-gray-400, #6a6a6a)
        );
      }

      :host([disabled]:hover) {
        --a3d-button-background-color: var(
          --a3d-button-background-color-override,
          var(--spectrum-actionbutton-quiet-background-color, transparent)
        );
        --a3d-button-border-color: var(
          --a3d-button-border-color-override,
          var(--spectrum-alias-border-color-transparent, transparent)
        );
        --a3d-button-content-color: var(
          --a3d-button-content-color-disabled-hover-override,
          var(--spectrum-gray-400, #6a6a6a)
        );
      }
    }

    button {
      border-style: solid;
      border-width: 1px;

      border-radius: 8px;
      width: 40px;
      height: 40px;
      padding: 0 11px;
      box-sizing: border-box;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      background-color: var(--a3d-button-background-color);
      border-color: var(--a3d-button-border-color);
      color: var(--a3d-button-content-color);

      cursor: pointer;
    }

    :host([disabled]) button {
      cursor: default;
    }

    slot[name='icon'] {
      color: inherit;
      fill: currentColor;
    }
  `;
__decorate([
    property({ type: Boolean, reflect: true })
], A3dButton.prototype, "selected", void 0);
__decorate([
    property({ type: String })
], A3dButton.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], A3dButton.prototype, "disabled", void 0);
A3dButton = __decorate([
    customElement('a3d-button')
], A3dButton);
export { A3dButton };
